import type { IntegrityCatalogData } from "./integrity-catalog.js";

/**
 * Carga inicial dos catálogos normativos.
 *
 * ATENÇÃO — as tensões admissíveis vêm marcadas como PENDING_VALIDATION de
 * propósito: são valores de referência e precisam ser conferidos pela
 * engenharia contra a edição da norma efetivamente adotada pelo cliente antes
 * de serem usados em documento oficial. A interface mostra esse estado junto
 * do valor, e o catálogo é editável — nada aqui é definitivo nem hardcoded em
 * página.
 *
 * Material sem tabela cadastrada não recebe S "aproximado": o cálculo fica
 * bloqueado até que a tabela seja cadastrada (regra "não inventar S").
 */

const B313_2022 = "ASME-B313-2022";
const B313_2020 = "ASME-B313-2020";

const STRESS_SOURCE = "ASME B31.3, Tabela A-1 — tensão admissível básica (valor de referência, sujeito a conferência da edição adotada)";

/** A106 Gr. B e A53 Gr. B compartilham a mesma tensão admissível básica tabelada. */
const CARBON_STEEL_STRESS_POINTS = [
  { temperatureCelsius: 38, stressMPa: 137.9 },
  { temperatureCelsius: 93, stressMPa: 137.9 },
  { temperatureCelsius: 149, stressMPa: 137.9 },
  { temperatureCelsius: 204, stressMPa: 137.9 },
  { temperatureCelsius: 260, stressMPa: 130.3 },
  { temperatureCelsius: 316, stressMPa: 119.3 },
  { temperatureCelsius: 343, stressMPa: 117.2 },
  { temperatureCelsius: 371, stressMPa: 113.8 },
  { temperatureCelsius: 399, stressMPa: 89.6 },
  { temperatureCelsius: 427, stressMPa: 74.5 }
];

export function buildIntegrityCatalogSeed(): IntegrityCatalogData {
  return {
    editions: [
      { id: B313_2022, norm: "ASME B31.3", edition: "2022", isDefault: true },
      { id: B313_2020, norm: "ASME B31.3", edition: "2020", isDefault: false },
      { id: "API-570-4ED", norm: "API 570", edition: "4ª Edição", isDefault: true },
      { id: "SP0775-2018", norm: "NACE/AMPP SP0775", edition: "2018", isDefault: true },
      { id: "SP0775-2013", norm: "NACE SP0775", edition: "2013", isDefault: false }
    ],

    materials: [
      {
        id: "MAT-A106-B",
        specification: "ASTM A106",
        grade: "Gr. B",
        productForm: "SEAMLESS",
        materialClass: "FERRITIC",
        family: "Aço carbono",
        densityGramsPerCm3: 7.86,
        minTemperatureCelsius: -29,
        maxTemperatureCelsius: 427
      },
      {
        id: "MAT-A53-B-ERW",
        specification: "ASTM A53",
        grade: "Gr. B (ERW)",
        productForm: "ERW",
        materialClass: "FERRITIC",
        family: "Aço carbono",
        densityGramsPerCm3: 7.86,
        minTemperatureCelsius: -29,
        maxTemperatureCelsius: 427
      },
      {
        id: "MAT-A312-TP304",
        specification: "ASTM A312",
        grade: "TP304",
        productForm: "SEAMLESS",
        materialClass: "AUSTENITIC",
        family: "Aço inoxidável austenítico",
        densityGramsPerCm3: 7.9,
        minTemperatureCelsius: -198,
        maxTemperatureCelsius: 816
      },
      {
        id: "MAT-A312-TP316",
        specification: "ASTM A312",
        grade: "TP316",
        productForm: "SEAMLESS",
        materialClass: "AUSTENITIC",
        family: "Aço inoxidável austenítico",
        densityGramsPerCm3: 8.0,
        minTemperatureCelsius: -198,
        maxTemperatureCelsius: 816
      }
    ],

    // Somente os materiais com tabela cadastrada resolvem S automaticamente.
    // TP304 e TP316 entram sem tabela de propósito: ao selecioná-los o sistema
    // informa que a tensão não foi encontrada e bloqueia o cálculo.
    allowableStressTables: [
      {
        materialId: "MAT-A106-B",
        editionId: B313_2022,
        points: CARBON_STEEL_STRESS_POINTS,
        source: STRESS_SOURCE,
        verification: "PENDING_VALIDATION"
      },
      {
        materialId: "MAT-A53-B-ERW",
        editionId: B313_2022,
        points: CARBON_STEEL_STRESS_POINTS,
        source: STRESS_SOURCE,
        verification: "PENDING_VALIDATION"
      }
    ],

    qualityFactors: [
      { productForm: "SEAMLESS", value: 1.0, source: "ASME B31.3 — fator de qualidade da junta longitudinal Ej, tubo sem costura", verification: "PENDING_VALIDATION" },
      { productForm: "ERW", value: 0.85, source: "ASME B31.3 — fator de qualidade da junta longitudinal Ej, ERW", verification: "PENDING_VALIDATION" },
      { productForm: "EFW", value: 0.85, source: "ASME B31.3 — fator de qualidade da junta longitudinal Ej, EFW", verification: "PENDING_VALIDATION" },
      { productForm: "FURNACE_BUTT_WELDED", value: 0.6, source: "ASME B31.3 — fator de qualidade da junta longitudinal Ej, soldado de topo em forno", verification: "PENDING_VALIDATION" }
    ],

    // W = 1,00 até 510 °C. Acima disso a norma reduz W e não há linha
    // cadastrada: o sistema pede entrada manual com justificativa em vez de
    // assumir 1,00 onde ele não vale.
    weldJointFactors: [
      { materialClass: "FERRITIC", maxTemperatureCelsius: 510, value: 1.0, source: "ASME B31.3, Tabela 302.3.5 — W = 1,00 até 510 °C", verification: "PENDING_VALIDATION" },
      { materialClass: "AUSTENITIC", maxTemperatureCelsius: 510, value: 1.0, source: "ASME B31.3, Tabela 302.3.5 — W = 1,00 até 510 °C", verification: "PENDING_VALIDATION" },
      { materialClass: "OTHER_DUCTILE", maxTemperatureCelsius: 510, value: 1.0, source: "ASME B31.3, Tabela 302.3.5 — W = 1,00 até 510 °C", verification: "PENDING_VALIDATION" },
      { materialClass: "CAST_IRON", maxTemperatureCelsius: 510, value: 1.0, source: "ASME B31.3, Tabela 302.3.5 — W = 1,00 até 510 °C", verification: "PENDING_VALIDATION" }
    ],

    yCoefficients: [
      { materialClass: "FERRITIC", temperatureCelsius: -273, value: 0.4, source: "ASME B31.3, Tabela 304.1.1", verification: "PENDING_VALIDATION" },
      { materialClass: "FERRITIC", temperatureCelsius: 510, value: 0.5, source: "ASME B31.3, Tabela 304.1.1", verification: "PENDING_VALIDATION" },
      { materialClass: "FERRITIC", temperatureCelsius: 538, value: 0.7, source: "ASME B31.3, Tabela 304.1.1", verification: "PENDING_VALIDATION" },

      { materialClass: "AUSTENITIC", temperatureCelsius: -273, value: 0.4, source: "ASME B31.3, Tabela 304.1.1", verification: "PENDING_VALIDATION" },
      { materialClass: "AUSTENITIC", temperatureCelsius: 593, value: 0.5, source: "ASME B31.3, Tabela 304.1.1", verification: "PENDING_VALIDATION" },
      { materialClass: "AUSTENITIC", temperatureCelsius: 621, value: 0.7, source: "ASME B31.3, Tabela 304.1.1", verification: "PENDING_VALIDATION" },

      { materialClass: "OTHER_DUCTILE", temperatureCelsius: -273, value: 0.4, source: "ASME B31.3, Tabela 304.1.1", verification: "PENDING_VALIDATION" },
      { materialClass: "CAST_IRON", temperatureCelsius: -273, value: 0.0, source: "ASME B31.3, Tabela 304.1.1", verification: "PENDING_VALIDATION" }
    ],

    couponMaterials: [
      { id: "CPM-CARBON-STEEL", name: "Aço Carbono", densityGramsPerCm3: 7.86, source: "Densidade de referência do material", verification: "VALIDATED" },
      { id: "CPM-304", name: "Aço Inoxidável 304", densityGramsPerCm3: 7.9, source: "Densidade de referência do material", verification: "VALIDATED" },
      { id: "CPM-316", name: "Aço Inoxidável 316", densityGramsPerCm3: 8.0, source: "Densidade de referência do material", verification: "VALIDATED" },
      { id: "CPM-DUPLEX", name: "Duplex 2205", densityGramsPerCm3: 7.8, source: "Densidade de referência do material", verification: "VALIDATED" },
      { id: "CPM-COPPER", name: "Cobre", densityGramsPerCm3: 8.96, source: "Densidade de referência do material", verification: "VALIDATED" },
      { id: "CPM-ALUMINUM", name: "Alumínio", densityGramsPerCm3: 2.7, source: "Densidade de referência do material", verification: "VALIDATED" }
    ]
  };
}
